import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { mindtimeWazuhVulnerabilitiesApi } from 'src/api/mindtime/wazuhVulnerabilitiesApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { guardEdrPreviewAction } from 'src/mindtime/utils/edrPreviewGuard';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhVulnerabilitiesListQuery,
  WazuhVulnerabilitiesSummaryData,
  WazuhVulnerableEndpointItem,
  WazuhVulnerabilityItem,
} from 'src/types/mindtime/wazuhVulnerabilities';
import type { WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';

export type VulnerabilityFacets = {
  operatingSystems: string[];
  packages: string[];
};

export type VulnerabilityFilters = {
  severity?: string;
  status?: string;
  minCvss?: number;
  packageName?: string;
  osPlatform?: string;
  wazuhAgentId?: string;
  organizationId?: string;
};

export type VulnerabilitySortParams = {
  column: string;
  sort: 'asc' | 'desc';
};

export type VulnerabilitiesState = {
  loading: boolean;
  summary: WazuhVulnerabilitiesSummaryData | null;
  items: WazuhPagedData<WazuhVulnerabilityItem> | null;
  endpoints: WazuhPagedData<WazuhVulnerableEndpointItem> | null;
  facets: VulnerabilityFacets;
};

type BuildVulnerabilitiesQueryInput = {
  scope: WazuhScopeParams;
  profileOrgId?: string;
  isReseller: boolean;
  search?: string;
  filters?: VulnerabilityFilters;
  sortParams?: VulnerabilitySortParams | null;
  page?: number;
  pageSize?: number;
};

const emptyFacets = (): VulnerabilityFacets => ({
  operatingSystems: [],
  packages: [],
});

const emptyState = (): VulnerabilitiesState => ({
  loading: true,
  summary: null,
  items: null,
  endpoints: null,
  facets: emptyFacets(),
});

const mergeFacets = (
  items: WazuhVulnerabilityItem[],
  previous: VulnerabilityFacets
): VulnerabilityFacets => {
  const operatingSystems = new Set(previous.operatingSystems);
  const packages = new Set(previous.packages);

  items.forEach((item) => {
    if (item.osPlatform) {
      operatingSystems.add(item.osPlatform);
    }
    const packageName = item.packageName?.trim();
    if (packageName) {
      packages.add(packageName);
    }
  });

  return {
    operatingSystems: Array.from(operatingSystems).sort((a, b) => a.localeCompare(b)),
    packages: Array.from(packages).sort((a, b) => a.localeCompare(b)),
  };
};

const buildVulnerabilitiesQuery = ({
  scope,
  profileOrgId,
  isReseller,
  search = '',
  filters = {},
  selectedEndpointId,
  sortParams,
  page,
  pageSize,
}: BuildVulnerabilitiesQueryInput & { selectedEndpointId?: string | null }): WazuhVulnerabilitiesListQuery => {
  const { severity, status, minCvss, packageName, osPlatform, organizationId } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId;
  const effectiveScope = buildEdrApiScope({
    scope,
    profileOrgId,
    isReseller,
    pageOrganizationId: organizationId || undefined,
  });

  return {
    ...effectiveScope,
    ...(typeof page === 'number' ? { page } : {}),
    ...(typeof pageSize === 'number' ? { pageSize } : {}),
    ...(search ? { search } : {}),
    ...(severity ? { severity } : {}),
    ...(status ? { status } : {}),
    ...(typeof minCvss === 'number' ? { minCvss } : {}),
    ...(packageName ? { packageName } : {}),
    ...(osPlatform ? { osPlatform } : {}),
    ...(wazuhAgentId ? { wazuhAgentId } : {}),
    ...(sortParams ? { column: sortParams.column, sort: sortParams.sort } : {}),
  };
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useVulnerabilityExport = (
  search = '',
  filters: VulnerabilityFilters = {},
  sortParams: VulnerabilitySortParams | null = null
) => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { isPreviewMode } = useEdrDataGate('vulnerability-detection');
  const profileOrgId = userData?.organization?.id;
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    if (guardEdrPreviewAction(isPreviewMode, 'previewExportBlocked')) return;
    setExporting(true);
    mindtimeWazuhVulnerabilitiesApi
      .export(
        buildVulnerabilitiesQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          sortParams,
          selectedEndpointId,
        })
      )
      .then(({ blob, filename }) => {
        const name = filename?.trim() ? filename : 'vulnerabilities-export.csv';
        triggerBlobDownload(blob, name);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'useVulnerabilityExport');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => {
        setExporting(false);
      });
  }, [scope, profileOrgId, isReseller, search, filters, sortParams, selectedEndpointId, isPreviewMode]);

  return { exporting, handleExport };
};

export const useVulnerabilitiesData = (
  page = 0,
  pageSize = 50,
  search = '',
  filters: VulnerabilityFilters = {},
  sortParams: VulnerabilitySortParams | null = null
): VulnerabilitiesState => {
  const { scope, isReseller, selectedEndpointId } = useWazuhScope();
  const { userData } = useUserProfile();
  const { skipLive, wait, isPreviewMode, getPreviewState } = useEdrDataGate('vulnerability-detection');
  const { canWidget } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'vulnerability-detection');
  const canSummary = canWidget('summary');
  const canList = canWidget('list');
  const canVulnerableEndpoints = canWidget('vulnerableEndpoints');
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<VulnerabilitiesState>(emptyState);

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        resellerId: scope.resellerId,
        startDate: scope.startDate,
        endDate: scope.endDate,
        isReseller,
      }),
    [scope.resellerId, scope.startDate, scope.endDate, isReseller]
  );

  const { severity, status, minCvss, packageName, osPlatform, organizationId } = filters;
  const wazuhAgentId = selectedEndpointId?.trim() || filters.wazuhAgentId || '';

  useEffect(() => {
    setState((prev) => ({ ...prev, facets: emptyFacets() }));
  }, [scopeKey]);

  useEffect(() => {
    if (wait) return undefined;
    if (isPreviewMode) {
      setState(getPreviewState() as VulnerabilitiesState);
      return undefined;
    }

    if (!canSummary) {
      setState((prev) => ({ ...prev, summary: null }));
      return undefined;
    }

    let cancelled = false;
    const sharedParams = buildVulnerabilitiesQuery({
      scope,
      profileOrgId,
      isReseller,
      filters,
      selectedEndpointId,
    });

    mindtimeWazuhVulnerabilitiesApi
      .getSummary({
        resellerId: sharedParams.resellerId,
        organizationId: sharedParams.organizationId,
      })
      .then((summary) => {
        if (!cancelled) setState((prev) => ({ ...prev, summary }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useVulnerabilitiesData.summary');
          setState((prev) => ({ ...prev, summary: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scope, profileOrgId, isReseller, organizationId, isPreviewMode, wait, getPreviewState, canSummary]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canList) {
      setState((prev) => ({ ...prev, loading: false, items: null }));
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    const sharedParams = buildVulnerabilitiesQuery({
      scope,
      profileOrgId,
      isReseller,
      search,
      filters,
      selectedEndpointId,
    });
    const listParams = {
      ...sharedParams,
      page,
      pageSize,
      ...(sortParams ? { column: sortParams.column, sort: sortParams.sort } : {}),
    };

    mindtimeWazuhVulnerabilitiesApi
      .list(listParams)
      .then((items) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          items,
          facets: mergeFacets(items?.items ?? [], prev.facets),
        }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useVulnerabilitiesData.list');
          setState((prev) => ({ ...prev, loading: false, items: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    page,
    pageSize,
    search,
    severity,
    status,
    minCvss,
    packageName,
    osPlatform,
    wazuhAgentId,
    organizationId,
    sortParams?.column,
    sortParams?.sort,
    isPreviewMode,
    skipLive,
    canList,
  ]);

  useEffect(() => {
    if (skipLive) return undefined;
    if (!canVulnerableEndpoints) {
      setState((prev) => ({ ...prev, endpoints: null }));
      return undefined;
    }

    let cancelled = false;

    const sharedParams = buildVulnerabilitiesQuery({
      scope,
      profileOrgId,
      isReseller,
      search,
      filters,
      selectedEndpointId,
    });

    mindtimeWazuhVulnerabilitiesApi
      .listEndpoints({
        ...sharedParams,
        page: 0,
        pageSize,
      })
      .then((endpoints) => {
        if (!cancelled) setState((prev) => ({ ...prev, endpoints }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useVulnerabilitiesData.endpoints');
          setState((prev) => ({ ...prev, endpoints: null }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    pageSize,
    search,
    severity,
    status,
    minCvss,
    packageName,
    osPlatform,
    wazuhAgentId,
    organizationId,
    sortParams?.column,
    sortParams?.sort,
    isPreviewMode,
    skipLive,
    canVulnerableEndpoints,
  ]);

  return state;
};
